import type { ApiUsageFiltersDto } from "@repo/marketplace-admin"

export const USAGE_WINDOWS = [
	{ value: "24h", label: "24h", hours: 24 },
	{ value: "48h", label: "48h", hours: 48 },
	{ value: "72h", label: "72h", hours: 72 },
	{ value: "7d", label: "7d", hours: 24 * 7 },
	{ value: "30d", label: "30d", hours: 24 * 30 },
] as const

export type UsageWindow = (typeof USAGE_WINDOWS)[number]["value"]

export const ACCESS_MODES = [
	{ value: "all", label: "All" },
	{ value: "direct_key", label: "Direct key" },
	{ value: "on_behalf", label: "On behalf" },
	{ value: "app_only", label: "App-only" },
] as const

export type AccessModeParam = (typeof ACCESS_MODES)[number]["value"]

/** Every filter the page holds, mirrored into the route search parameters. */
export type UsageSearch = {
	window: UsageWindow
	accessMode: AccessModeParam
	/** Custom range, ISO-8601. Overrides `window` when both bounds are present. */
	from?: string
	to?: string
	appId?: string
	accountId?: string
	endpoint?: string
	tag?: string
	/** Grouping of the top-callers table. */
	group?: "account" | "app" | "appAccount"
	/** Stacking dimension of the volume chart. */
	stack?: "accessMode" | "app" | "tag"
}

function isWindow(value: unknown): value is UsageWindow {
	return USAGE_WINDOWS.some((entry) => entry.value === value)
}

function isAccessMode(value: unknown): value is AccessModeParam {
	return ACCESS_MODES.some((entry) => entry.value === value)
}

function optionalString(value: unknown): string | undefined {
	return typeof value === "string" && value.length > 0 ? value : undefined
}

/**
 * Search parameters are untrusted. An unparseable bound would reach
 * `toWireFilters` and throw `RangeError` out of `toISOString()` during render,
 * so it is dropped here and the window preset takes over.
 */
function optionalTimestamp(value: unknown): string | undefined {
	const text = optionalString(value)
	if (!text) return undefined
	return Number.isNaN(new Date(text).getTime()) ? undefined : text
}

export function validateUsageSearch(
	search: Record<string, unknown>,
): UsageSearch {
	const group = optionalString(search.group)
	const stack = optionalString(search.stack)
	return {
		window: isWindow(search.window) ? search.window : "24h",
		accessMode: isAccessMode(search.accessMode) ? search.accessMode : "all",
		from: optionalTimestamp(search.from),
		to: optionalTimestamp(search.to),
		appId: optionalString(search.appId),
		accountId: optionalString(search.accountId),
		endpoint: optionalString(search.endpoint),
		tag: optionalString(search.tag),
		group:
			group === "account" || group === "app" || group === "appAccount"
				? group
				: "account",
		stack:
			stack === "accessMode" || stack === "app" || stack === "tag"
				? stack
				: "accessMode",
	}
}

/**
 * Resolves the search parameters into the wire filter. The window is anchored
 * to the end of the current hour so the bucket in progress is included — the
 * recorder flushes into it every sixty seconds, which is what makes the top of
 * the page a live throughput figure.
 */
export function toWireFilters(search: UsageSearch): ApiUsageFiltersDto {
	const { from, to } = resolveRange(search)
	return {
		from: from.toISOString(),
		to: to.toISOString(),
		accessMode:
			search.accessMode === "all"
				? undefined
				: (search.accessMode as ApiUsageFiltersDto["accessMode"]),
		appId: search.appId,
		accountId: search.accountId,
		endpoint: search.endpoint,
		tag: search.tag,
	}
}

export function resolveRange(search: UsageSearch): { from: Date; to: Date } {
	if (search.from && search.to) {
		return { from: new Date(search.from), to: new Date(search.to) }
	}
	const hours =
		USAGE_WINDOWS.find((entry) => entry.value === search.window)?.hours ?? 24

	const to = new Date()
	to.setUTCMinutes(0, 0, 0)
	to.setUTCHours(to.getUTCHours() + 1)
	return { from: new Date(to.getTime() - hours * 3_600_000), to }
}

export type FilterChip = {
	key: keyof UsageSearch
	label: string
	value: string
}

/** The removable chips shown beneath the window selector. */
export function activeChips(
	search: UsageSearch,
	names: { appName?: string; accountName?: string },
): FilterChip[] {
	const chips: FilterChip[] = []
	if (search.appId) {
		chips.push({
			key: "appId",
			label: "Application",
			value: names.appName ?? search.appId,
		})
	}
	if (search.accountId) {
		chips.push({
			key: "accountId",
			label: "Account",
			value: names.accountName ?? search.accountId,
		})
	}
	if (search.endpoint) {
		chips.push({ key: "endpoint", label: "Endpoint", value: search.endpoint })
	}
	if (search.tag) {
		chips.push({ key: "tag", label: "Category", value: search.tag })
	}
	if (search.accessMode !== "all") {
		chips.push({
			key: "accessMode",
			label: "Access",
			value:
				ACCESS_MODES.find((mode) => mode.value === search.accessMode)?.label ??
				search.accessMode,
		})
	}
	return chips
}
